import { AlertCircle, AtSign, ExternalLink, HardDrive, Inbox, Loader2, Pause, Play, RefreshCw, Send, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Avatar } from '@/components/ui/avatar'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Kpi } from '@/features/finance/components/FinanceBits'
import { toastError } from '@/features/finance/ui'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtRelative } from '../shared/format'
import { useMailAccounts, useResyncMail, useToggleMailPause } from './api'
import { Toolbar, UsageBar } from './components/AdminBits'
import { MailProvidersPanel } from './components/MailProvidersPanel'
import type { MailAccount, MailAccountStatus } from './types'
import { includesTr } from './ui'

const STATUS_TONE: Record<MailAccountStatus, BadgeTone> = { connected: 'success', error: 'danger', paused: 'neutral' }

export function MailAccountsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const list = useMailAccounts()
  const [q, setQ] = useState('')
  const [purpose, setPurpose] = useState<MailAccount['purpose'] | 'all'>('all')
  const canWrite = can('mail:account:manage')
  const canProviders = can('mail:provider:manage')
  const all = list.data ?? []
  const rows = all.filter((m) => (purpose === 'all' || m.purpose === purpose) && includesTr(`${m.email} ${m.displayName} ${m.members.join(' ')}`, q))
  const errors = all.filter((m) => m.status === 'error')
  const used = all.reduce((n, m) => n + m.quotaUsedGb, 0)
  const quota = all.reduce((n, m) => n + m.quotaGb, 0)

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.admin')}
        title={t('admin.mail.title')}
        description={t('admin.mail.subtitle')}
        actions={
          <Button variant="outline" asChild>
            <Link to="/mail">
              <ExternalLink />
              {t('admin.mail.openMailbox')}
            </Link>
          </Button>
        }
      />

      {canProviders ? (
        <div className="mb-4">
          <MailProvidersPanel />
        </div>
      ) : null}

      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : (
        <div className="space-y-4">
          {errors.length ? (
            <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/[0.05] p-4">
              <AlertCircle className="size-5 shrink-0 text-destructive" aria-hidden />
              <div className="min-w-0 text-sm">
                <p className="font-semibold">{t('admin.mail.errorBanner', { count: errors.length })}</p>
                <p className="text-muted-foreground">{errors.map((e) => `${e.email}: ${e.error}`).join(' · ')}</p>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi icon={AtSign} tone="primary" label={t('admin.mail.kpi.accounts')} value={all.filter((m) => m.status === 'connected').length} footer={t('admin.mail.kpi.accountsHint', { count: all.length })} />
            <Kpi icon={Users} tone="info" label={t('admin.mail.kpi.shared')} value={all.filter((m) => m.purpose === 'shared').length} footer={t('admin.mail.kpi.sharedHint', { count: new Set(all.flatMap((m) => m.members)).size })} />
            <Kpi icon={Send} tone="success" label={t('admin.mail.kpi.sent')} value={all.reduce((n, m) => n + m.sentToday, 0)} footer={t('admin.mail.kpi.sentHint')} />
            <Kpi icon={HardDrive} tone="warning" label={t('admin.mail.kpi.storage')} value={`${used.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} GB`} footer={t('admin.mail.kpi.storageHint', { total: quota })} />
          </div>

          <section className="overflow-hidden rounded-2xl border bg-card shadow-xs">
            <Toolbar q={q} onQ={setQ} placeholder={t('admin.mail.search')} count={t('admin.common.count', { count: rows.length })}>
              <Select
                className="w-40"
                value={purpose}
                onChange={setPurpose}
                aria-label={t('admin.mail.purpose')}
                options={[{ value: 'all', label: t('admin.mail.allPurposes') }, ...(['shared', 'personal', 'system'] as const).map((p) => ({ value: p, label: t(`admin.mail.purposes.${p}`) }))]}
              />
            </Toolbar>
            {rows.length === 0 ? (
              <EmptyState icon={Inbox} title={t('admin.common.noMatch')} />
            ) : (
              <ul className="divide-y">
                {rows.map((m) => (
                  <MailRow key={m.id} account={m} canWrite={canWrite} />
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </>
  )
}

function MailRow({ account: m, canWrite }: { account: MailAccount; canWrite: boolean }) {
  const { t } = useTranslation()
  const resync = useResyncMail()
  const pause = useToggleMailPause()
  const quotaPct = (m.quotaUsedGb / m.quotaGb) * 100
  const sendPct = (m.sentToday / m.dailyLimit) * 100

  return (
    <li className={cn('grid grid-cols-1 gap-4 px-4 py-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-center', m.status === 'paused' && 'opacity-70')}>
      <div className="flex min-w-0 items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border bg-muted/50 text-xs font-bold uppercase text-muted-foreground">{m.provider === 'google' ? 'G' : m.provider === 'microsoft' ? 'MS' : '@'}</span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate font-medium">{m.email}</span>
            <Badge tone={STATUS_TONE[m.status]}>{t(`admin.mail.status.${m.status}`)}</Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {m.displayName} · {t(`admin.mail.providers.${m.provider}`)} · {t(`admin.mail.purposes.${m.purpose}`)}
          </p>
          <p className={cn('text-xs', m.status === 'error' ? 'text-destructive' : 'text-muted-foreground')}>
            {m.status === 'error' ? m.error : t('admin.mail.lastSync', { when: fmtRelative(m.lastSyncAt) })}
          </p>
        </div>
      </div>
      <div className="flex min-w-0 items-center gap-2">
        {m.members.length ? (
          <>
            <div className="flex -space-x-2">
              {m.members.slice(0, 4).map((n) => (
                <span key={n} title={n}>
                  <Avatar name={n} className="size-7 border-2 border-card [&_span]:text-[10px]" />
                </span>
              ))}
            </div>
            <span className="truncate text-xs text-muted-foreground">{t('admin.mail.members', { count: m.members.length })}</span>
          </>
        ) : (
          <span className="text-xs text-muted-foreground">{t('admin.mail.noMembers')}</span>
        )}
      </div>
      <div className="space-y-2">
        <UsageBar value={quotaPct} label={t('admin.mail.quota', { used: m.quotaUsedGb.toLocaleString('tr-TR'), total: m.quotaGb })} />
        <UsageBar value={sendPct} label={t('admin.mail.sendLimit', { sent: m.sentToday, limit: m.dailyLimit.toLocaleString('tr-TR') })} />
      </div>
      {canWrite ? (
        <div className="flex gap-1.5 lg:justify-end">
          <Button
            size="sm"
            variant={m.status === 'error' ? 'default' : 'outline'}
            disabled={resync.isPending || m.status === 'paused'}
            onClick={() => resync.mutate(m.id, { onSuccess: () => toast.success(t('admin.mail.synced')), onError: toastError })}
          >
            {resync.isPending ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            {m.status === 'error' ? t('admin.mail.reconnect') : t('admin.mail.sync')}
          </Button>
          {m.status !== 'error' ? (
            <Button
              size="icon"
              variant="ghost"
              className="size-8"
              aria-label={m.status === 'paused' ? t('admin.mail.resume') : t('admin.mail.pause')}
              title={m.status === 'paused' ? t('admin.mail.resume') : t('admin.mail.pause')}
              disabled={pause.isPending}
              onClick={() => pause.mutate(m.id, { onError: toastError })}
            >
              {m.status === 'paused' ? <Play /> : <Pause />}
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}
